/**
 * Delivery date rules for quote-to-order conversion.
 *
 * A "working day" is Monday-Friday, excluding configured bank holidays.
 * The earliest selectable delivery date is the Nth working day after the
 * reference date (the day of conversion), where N = buffer days.
 */

import dayjs, { Dayjs } from 'dayjs';
import { IHoliday } from '../types';

/** Expands each holiday's [scheduledStart, scheduledEnd] range into every date it covers */
export const toHolidaySet = (holidays: IHoliday[]): Set<string> => {
  const dates = new Set<string>();
  for (const h of holidays) {
    let cursor = dayjs(h.scheduledStart).startOf('day');
    const end = dayjs(h.scheduledEnd).startOf('day');
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
      dates.add(cursor.format('YYYY-MM-DD'));
      cursor = cursor.add(1, 'day');
    }
  }
  return dates;
};

export const isWeekend = (date: Dayjs): boolean => {
  const day = date.day(); // 0 = Sunday, 6 = Saturday
  return day === 0 || day === 6;
};

export const isBankHoliday = (date: Dayjs, holidayDates: Set<string>): boolean =>
  holidayDates.has(date.format('YYYY-MM-DD'));

export const isWorkingDay = (date: Dayjs, holidayDates: Set<string>): boolean =>
  !isWeekend(date) && !isBankHoliday(date, holidayDates);

/**
 * Earliest selectable delivery date: walk forward from `fromDate`, counting
 * working days, until `bufferDays` of them have been counted.
 */
export const calculateEarliestDeliveryDate = (
  fromDate: Dayjs,
  bufferDays: number,
  holidayDates: Set<string>,
): Dayjs => {
  let cursor = fromDate.startOf('day');
  let counted = 0;
  while (counted < bufferDays) {
    cursor = cursor.add(1, 'day');
    if (isWorkingDay(cursor, holidayDates)) counted += 1;
  }
  return cursor;
};

/**
 * disabledDate predicate for the delivery-date picker: weekends, configured
 * bank holidays, and anything before the buffer-day window are unselectable.
 */
export const isDeliveryDateDisabled = (
  date: Dayjs,
  fromDate: Dayjs,
  bufferDays: number,
  holidayDates: Set<string>,
): boolean => {
  if (isWeekend(date)) return true;
  if (isBankHoliday(date, holidayDates)) return true;
  const earliest = calculateEarliestDeliveryDate(fromDate, bufferDays, holidayDates);
  return date.startOf('day').isBefore(earliest.startOf('day'));
};

export const today = (): Dayjs => dayjs();
